import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Inbox, Users, FileText, GraduationCap, TrendingUp, CheckCircle2, FileSignature, ArrowRight, Send } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

function StatTile({ icon: Icon, label, value, sub, tid, to, accent }) {
  const cls = "block bg-white border border-slate-200 p-6 hover:border-[#3FBAB0] hover:shadow-md transition-all cursor-pointer group";
  return (
    <Link to={to} className={cls} data-testid={tid}>
      <div className="flex items-center justify-between mb-6">
        <Icon className={`w-5 h-5 ${accent || "text-[#1FA59A]"}`} strokeWidth={1.5} />
        <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-[#3FBAB0] group-hover:translate-x-1 transition-all" />
      </div>
      <p className="font-display text-4xl font-bold text-slate-900">{value ?? "—"}</p>
      <p className="text-sm text-slate-500 mt-2">{label}</p>
      {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
    </Link>
  );
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [indexNowBusy, setIndexNowBusy] = useState(false);
  const [indexNowMeta, setIndexNowMeta] = useState(null);

  useEffect(() => {
    if (user?.role === "admin") {
      api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => setStats({}));
      api.get("/indexnow/status").then((r) => setIndexNowMeta(r.data)).catch(() => {});
    } else {
      api.get("/enquiries").then((r) => setStats({ recent_enquiries: r.data.slice(0, 5), total_enquiries: r.data.length, new_enquiries: r.data.filter(e => e.status === "new").length }));
    }
  }, [user]);

  const submitAllToBing = async () => {
    if (indexNowBusy) return;
    if (!window.confirm(`Submit all ${indexNowMeta?.sitemap_url_count || ""} URLs from sitemap.xml to Bing / Yandex via IndexNow?`)) return;
    setIndexNowBusy(true);
    try {
      const r = await api.post("/indexnow/submit-all");
      if (r.data?.ok) {
        toast.success(`Submitted ${r.data.submitted_count || r.data.count} URLs to Bing (HTTP ${r.data.status})`);
      } else {
        toast.error(`IndexNow submission failed: ${r.data?.reason || r.data?.body || "unknown error"}`);
      }
    } catch (err) {
      toast.error(`IndexNow request failed: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setIndexNowBusy(false);
    }
  };

  return (
    <div className="p-8 lg:p-10" data-testid="page-admin-dashboard">
      <div className="mb-8">
        <p className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">Dashboard</p>
        <h1 className="font-display text-3xl lg:text-4xl font-bold text-slate-900">Welcome back, {user?.name?.split(" ")[0]}.</h1>
      </div>

      {!stats ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatTile icon={Inbox} label="Total Enquiries" value={stats.total_enquiries} tid="stat-enquiries" to="/admin/enquiries" />
            <StatTile icon={TrendingUp} label="New (unassigned)" value={stats.new_enquiries} tid="stat-new" to="/admin/enquiries" sub="Click to triage" />
            {user?.role === "admin" && (
              <>
                <StatTile icon={FileSignature} label="Quotes issued" value={stats.total_quotes} tid="stat-quotes" to="/admin/enquiries" />
                <StatTile icon={CheckCircle2} label="Completed jobs" value={stats.completed_enquiries} tid="stat-completed" to="/admin/enquiries" />
                <StatTile icon={Users} label="Users" value={stats.total_users} tid="stat-users" to="/admin/users" />
                <StatTile icon={FileText} label="Published posts" value={stats.total_posts} tid="stat-posts" to="/admin/blog" />
                <StatTile icon={GraduationCap} label="Training modules" value={stats.total_modules} tid="stat-modules" to="/admin/training" />
                <StatTile icon={CheckCircle2} label="Training completions" value={stats.total_completions} tid="stat-completions" to="/admin/training" />
              </>
            )}
          </div>

          {user?.role === "admin" && (
            <div className="bg-white border border-slate-200 mb-8" data-testid="dash-seo-panel">
              <div className="flex items-start justify-between p-6 border-b border-slate-200 gap-4">
                <div>
                  <h2 className="font-display text-xl font-semibold text-slate-900">Search engine indexing</h2>
                  <p className="text-sm text-slate-500 mt-1">
                    Push every URL in <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">sitemap.xml</code> to Bing &amp; Yandex via IndexNow. Useful after a content drop or rebrand.
                  </p>
                  {indexNowMeta && (
                    <p className="text-xs text-slate-400 mt-2">
                      Host: <span className="font-mono">{indexNowMeta.host}</span> · Sitemap URLs: <span className="font-mono">{indexNowMeta.sitemap_url_count}</span> · Status: {indexNowMeta.configured ? <span className="text-emerald-600 font-semibold">configured</span> : <span className="text-red-600 font-semibold">missing key</span>}
                    </p>
                  )}
                </div>
                <button
                  onClick={submitAllToBing}
                  disabled={indexNowBusy || !indexNowMeta?.configured}
                  className="flex-shrink-0 inline-flex items-center gap-2 bg-[#1FA59A] text-white text-sm font-semibold px-4 py-2.5 rounded hover:bg-[#3FBAB0] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  data-testid="submit-all-to-bing-btn"
                >
                  {indexNowBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {indexNowBusy ? "Submitting…" : "Submit all to Bing"}
                </button>
              </div>
            </div>
          )}

          <div className="bg-white border border-slate-200">
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <h2 className="font-display text-xl font-semibold text-slate-900">Recent enquiries</h2>
              <Link to="/admin/enquiries" className="text-sm text-[#1FA59A] font-semibold hover:underline" data-testid="dash-view-all-enquiries">View all →</Link>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="px-6 py-3 font-medium">Name</th>
                  <th className="px-6 py-3 font-medium">Service</th>
                  <th className="px-6 py-3 font-medium">Status</th>
                  <th className="px-6 py-3 font-medium">Date</th>
                </tr>
              </thead>
              <tbody>
                {(stats.recent_enquiries || []).length === 0 && (
                  <tr><td colSpan={4} className="px-6 py-10 text-center text-slate-400">No enquiries yet.</td></tr>
                )}
                {(stats.recent_enquiries || []).map((e) => (
                  <tr key={e.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`dash-enquiry-${e.id}`}>
                    <td className="px-6 py-4">
                      <p className="font-medium text-slate-900">{e.name}</p>
                      <p className="text-xs text-slate-500">{e.email}</p>
                    </td>
                    <td className="px-6 py-4 text-slate-600">{e.service || "General"}</td>
                    <td className="px-6 py-4">
                      <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 ${
                        e.status === "new" ? "bg-blue-50 text-blue-700" :
                        e.status === "won" ? "bg-emerald-50 text-emerald-700" :
                        e.status === "lost" ? "bg-red-50 text-red-700" : "bg-slate-100 text-slate-700"
                      }`}>{e.status}</span>
                    </td>
                    <td className="px-6 py-4 text-slate-500">{new Date(e.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
